import { Message } from "primereact/message";

import {
  ConfirmDialog,
  Dialog,
  DialogFooter,
  DialogHeader,
  Dropdown,
  ErrorState,
  ForbiddenState,
  FormField,
  Icon,
  StateMessage,
  Stepper,
} from "@/ds";
import { AjustarProjecaoTransferenciaDialog } from "../ajustar-projecao-transferencia-dialog";
import { EtapaDestinoTransferencia } from "../etapa-destino-transferencia";
import { EscopoCartao } from "../escopo-cartao";
import { JustificativaField } from "@/shared/components";
import { RevisaoTransferenciaTipo } from "./revisao-transferencia-tipo";
import { CARREGANDO_REVISAO_LABEL } from "./revisao-transferencia-tipo/revisao-transferencia-tipo.constants";
import {
  CANCELAR_LABEL,
  CARREGANDO_LABEL,
  CENTRO_CUSTO_LABEL,
  CENTRO_CUSTO_PLACEHOLDER,
  CONFIRMAR_TITLE,
  ETAPAS_LABEL,
  PROXIMA_LABEL,
  SOLICITAR_LABEL,
  TRANSFERIR_TIPO_TITLE,
  VOLTAR_LABEL,
} from "./transferir-tipo-obra-dialog.constants";
import modal from "../planejamento-controle.modal.module.scss";
import styles from "./transferir-tipo-obra-dialog.module.scss";
import type { TransferirTipoObraDialogViewModel } from "./types-transferir-tipo-obra-dialog";

const ROTULO = { label: modal.rotuloCampo };
const PAINEL = { panel: { className: modal.painel } };
const RODAPE_DIALOGO = { footer: { className: modal.dialogoRodape } };

export function TransferirTipoObraDialogView({
  formId,
  estado,
  errorMessage,
  recarregar,
  etapa,
  etapas,
  cartao,
  avisoSemObras,
  etapaDestino,
  semObraDisponivel,
  centroCusto,
  destinoErro,
  revisao,
  ajustar,
  revisaoEstado,
  proximaDesabilitada,
  transferirDesabilitado,
  avancar,
  voltar,
  abrirConfirmacao,
  confirmacao,
  cancelar,
  descarte,
}: TransferirTipoObraDialogViewModel) {
  const naRevisao = etapa === 2;
  const comEtapas = estado === "vazio" || estado === "pronto";

  return (
    <>
      <Dialog
        visible
        size="xl"
        className={modal.dialogoFicha}
        onHide={cancelar}
        focusOnShow={false}
        header={<DialogHeader title={TRANSFERIR_TIPO_TITLE} />}
        pt={RODAPE_DIALOGO}
        footer={
          <DialogFooter
            className={modal.rodape}
            cancelLabel={naRevisao ? VOLTAR_LABEL : CANCELAR_LABEL}
            confirmLabel={naRevisao ? SOLICITAR_LABEL : PROXIMA_LABEL}
            confirmButtonProps={{
              disabled: naRevisao ? transferirDesabilitado : proximaDesabilitada,
            }}
            onCancel={naRevisao ? voltar : cancelar}
            onConfirm={naRevisao ? abrirConfirmacao : avancar}
            testId="transferir-tipo"
          />
        }
        data-testid="transferir-tipo-obra-dialog"
      >
        <div className={styles.corpo}>
          <EscopoCartao
            {...cartao}
            testIds={{
              cartao: "transferir-tipo-cartao",
              projecao: "transferir-tipo-projecao",
              nome: "transferir-tipo-nome",
              contexto: "transferir-tipo-contexto",
            }}
          />

          {estado === "carregando" ? (
            <StateMessage
              variant="loading"
              title={CARREGANDO_LABEL}
              testId="transferir-tipo-loading"
            />
          ) : null}
          {estado === "proibido" ? (
            <ForbiddenState
              description={errorMessage ?? undefined}
              testId="transferir-tipo-error"
            />
          ) : null}
          {estado === "erro" ? (
            <ErrorState
              message={errorMessage ?? undefined}
              onRetry={recarregar}
              testId="transferir-tipo-error"
            />
          ) : null}

          {comEtapas ? (
            <Stepper.Root aria-label={ETAPAS_LABEL}>
              {etapas.map(({ numero, titulo, situacao, ariaLabel, testId, onSelect }) => (
                <Stepper.Step
                  key={numero}
                  state={situacao}
                  number={numero}
                  title={titulo}
                  ariaLabel={ariaLabel}
                  onSelect={onSelect}
                  testId={testId}
                />
              ))}
            </Stepper.Root>
          ) : null}

          {estado === "vazio" || (estado === "pronto" && semObraDisponivel) ? (
            <Message
              severity="warn"
              className="p-message-simple p-message-block"
              icon={<Icon name="info-circle" size={18} />}
              text={avisoSemObras}
              data-testid="transferir-tipo-sem-obras"
            />
          ) : null}

          {estado === "pronto" && !naRevisao ? (
            <EtapaDestinoTransferencia {...etapaDestino}>
              {destinoErro ? (
                <Message
                  className="p-message-block"
                  severity="error"
                  text={destinoErro}
                  data-testid="transferir-tipo-destino-erro"
                />
              ) : null}
              {centroCusto.mostrar ? (
                <div className={styles.destinoCampos}>
                  <FormField.Root required classNames={ROTULO} error={centroCusto.erro}>
                    <FormField.Label>{CENTRO_CUSTO_LABEL}</FormField.Label>
                    <FormField.Control>
                      <Dropdown
                        appendTo="self"
                        pt={PAINEL}
                        value={centroCusto.value}
                        options={centroCusto.opcoes}
                        placeholder={CENTRO_CUSTO_PLACEHOLDER}
                        onChange={(event) => centroCusto.escolher(event.value)}
                        data-testid="transferir-tipo-centro-custo"
                      />
                    </FormField.Control>
                    <FormField.Error data-testid="transferir-tipo-centro-custo-error" />
                  </FormField.Root>
                </div>
              ) : null}
              {centroCusto.erro && !centroCusto.mostrar ? (
                <Message
                  className="p-message-block"
                  severity="error"
                  text={centroCusto.erro}
                  data-testid="transferir-tipo-sem-centro-custo"
                />
              ) : null}
              {centroCusto.incorporacao ? (
                <Message
                  className="p-message-block"
                  severity="info"
                  text={centroCusto.incorporacao}
                  data-testid="transferir-tipo-ja-existe"
                />
              ) : null}
            </EtapaDestinoTransferencia>
          ) : null}

          {estado === "pronto" && naRevisao && revisaoEstado.carregando ? (
            <StateMessage
              variant="loading"
              title={CARREGANDO_REVISAO_LABEL}
              testId="transferir-tipo-revisao-loading"
            />
          ) : null}
          {estado === "pronto" && naRevisao && revisaoEstado.erro ? (
            <ErrorState
              message={revisaoEstado.erro}
              onRetry={revisaoEstado.recarregar}
              testId="transferir-tipo-revisao-error"
            />
          ) : null}
          {estado === "pronto" && naRevisao && revisaoEstado.erroEnvio ? (
            <Message
              className="p-message-block"
              severity="error"
              text={revisaoEstado.erroEnvio}
              data-testid="transferir-tipo-erro-envio"
            />
          ) : null}
          {estado === "pronto" && naRevisao && revisao ? (
            <RevisaoTransferenciaTipo {...revisao} />
          ) : null}
        </div>
      </Dialog>

      {confirmacao.aberta ? (
        <Dialog
          visible
          size="md"
          className={styles.dialogoConfirmacao}
          onHide={confirmacao.cancelar}
          locked={confirmacao.isSubmitting}
          focusOnShow={false}
          header={<DialogHeader title={CONFIRMAR_TITLE} />}
          pt={RODAPE_DIALOGO}
          footer={
            <DialogFooter
              className={modal.rodape}
              cancelLabel={CANCELAR_LABEL}
              confirmLabel={SOLICITAR_LABEL}
              formId={formId}
              loading={confirmacao.isSubmitting}
              locked={confirmacao.isSubmitting}
              onCancel={confirmacao.cancelar}
              testId="transferir-tipo-confirmar"
            />
          }
          data-testid="transferir-tipo-confirmacao-dialog"
        >
          <form id={formId} className={styles.confirmacao} onSubmit={confirmacao.enviar} noValidate>
            <p className={styles.pergunta} data-testid="transferir-tipo-confirmar-mensagem">
              {confirmacao.mensagem}
            </p>
            <JustificativaField
              labelClassName={modal.rotuloCampo}
              value={confirmacao.justificativa}
              onChange={confirmacao.digitarJustificativa}
              error={confirmacao.justificativaError}
              disabled={confirmacao.isSubmitting}
              testIds={{
                campo: "transferir-tipo-justificativa",
                contador: "transferir-tipo-contador",
                erro: "transferir-tipo-justificativa-error",
              }}
            />
            <Message
              className="p-message-block"
              severity="info"
              text={confirmacao.avisoTransferencia}
              data-testid="transferir-tipo-aviso-transferencia"
            />
            {confirmacao.submitError ? (
              <Message
                className="p-message-block"
                severity="error"
                text={confirmacao.submitError}
                data-testid="transferir-tipo-submit-error"
              />
            ) : null}
          </form>
        </Dialog>
      ) : null}

      {ajustar ? <AjustarProjecaoTransferenciaDialog {...ajustar} /> : null}
      {descarte ? <ConfirmDialog {...descarte} /> : null}
    </>
  );
}
